import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { ChevronRight, Siren, DoorOpen, KeyRound, Shield, RectangleVertical, Hammer, Lock, Phone } from "lucide-react";
import { Reveal } from "./Reveal";
import { PHONE, PHONE_INTL, SITE_URL } from "../lib/api";

export const ICONS = { siren: Siren, "door-open": DoorOpen, "key-round": KeyRound, shield: Shield, "rectangle-vertical": RectangleVertical, hammer: Hammer, lock: Lock };

export const Breadcrumbs = ({ items }) => {
  useEffect(() => {
    const origin = SITE_URL;
    const data = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: items.map((it, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: it.label,
        ...(it.to ? { item: `${origin}${it.to}` } : {}),
      })),
    };
    let el = document.getElementById("ld-breadcrumb");
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = "ld-breadcrumb";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
    return () => {
      const node = document.getElementById("ld-breadcrumb");
      if (node) node.remove();
    };
  }, [items]);

  return (
    <nav className="flex items-center flex-wrap gap-1.5 text-xs text-zinc-500 mb-8" data-testid="breadcrumbs" aria-label="Fil d'Ariane">
      {items.map((it, i) => (
        <span key={i} className="flex items-center gap-1.5">
          {i > 0 && <ChevronRight className="h-3 w-3 text-zinc-300" />}
          {it.to ? <Link to={it.to} className="hover:text-primary">{it.label}</Link> : <span className="text-zinc-900 font-medium">{it.label}</span>}
        </span>
      ))}
    </nav>
  );
};

export const FaqAccordion = ({ items, title = "Questions fréquentes", subtitle }) => (
  <section className="relative z-10 py-24 sm:py-32 bg-white" data-testid="faq-section">
    <div className="max-w-3xl mx-auto px-6">
      <Reveal>
        <p className="overline mb-4">FAQ</p>
        <h2 className="text-4xl sm:text-5xl font-display font-semibold tracking-tight mb-3 text-zinc-900">{title}</h2>
        {subtitle && <p className="text-zinc-500 mb-10">{subtitle}</p>}
      </Reveal>
      <Reveal delay={0.1}>
        <Accordion type="single" collapsible className="w-full">
          {items.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-zinc-200" data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-left text-lg font-medium text-zinc-900 hover:text-primary hover:no-underline py-6">{f.q}</AccordionTrigger>
              <AccordionContent className="text-zinc-600 text-base leading-relaxed pb-6">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);

export const CtaBand = ({ title = "Une urgence serrurerie ?", text = "Un serrurier se met en route dès votre appel. Devis gratuit, intervention en 30 minutes." }) => (
  <section className="relative z-10 py-24 bg-white" data-testid="cta-band">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <Reveal className="relative overflow-hidden rounded-3xl bg-primary p-10 sm:p-16 shadow-xl shadow-primary/20">
        <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -left-16 -bottom-16 h-60 w-60 rounded-full bg-black/10 blur-3xl" />
        <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-8">
          <div>
            <h2 className="text-4xl sm:text-5xl font-display font-semibold tracking-tight max-w-xl text-white">{title}</h2>
            <p className="text-indigo-100 mt-4 max-w-lg">{text}</p>
          </div>
          <div className="flex flex-col gap-3 shrink-0">
            <a href={`tel:${PHONE_INTL}`} data-testid="cta-call" className="inline-flex items-center justify-center gap-2.5 rounded-full bg-white px-8 py-4 text-base font-semibold text-primary hover:scale-105 transition-transform duration-200">
              <Phone className="h-5 w-5" /> {PHONE}
            </a>
            <Link to="/contact" className="text-center text-sm text-indigo-100 hover:text-white link-underline" data-testid="cta-quote">Ou demander un devis en ligne →</Link>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
